export type ArtifactThemeMode = "light" | "dark";

export type ArtifactFilterKind = "Value" | "AsOfDate" | "Period";

export type ArtifactDensity = "Compact" | "Comfortable";

export type ArtifactColumnType = "Text" | "Numeric" | "Date" | "DateTime" | "Boolean";

export type ArtifactMeasurePeriod = "NetChange" | "BeginningBalance" | "EndingBalance" | "Mtd" | "Qtd" | "Ytd" | "Ltm";

export interface ArtifactSourceRef {
  key: string;
  boundFilterKeys: string[];
  linkFilterKeys: string[];
}

export interface ArtifactFilterRef {
  key: string;
  label: string;
  kind: ArtifactFilterKind;
  singleSelect: boolean;
}

export interface ArtifactViewRef {
  key: string;
  title: string;
  linkFilterKeys: string[];
}

export interface ArtifactStyleTokens {
  background: string;
  paper: string;
  textPrimary: string;
  textSecondary: string;
  primary: string;
  secondary: string;
  chart?: string[];
  positive?: string;
  negative?: string;
  reference?: string;
  warning?: string;
}

export interface ArtifactStyle {
  light: ArtifactStyleTokens;
  dark: ArtifactStyleTokens;
  radius: number;
  density: ArtifactDensity;
  bodyFont?: string;
  headingFont?: string;
}

export interface ArtifactPeriodRange {
  values: string[];
  equalityType: string;
}

export interface ArtifactSourceColumn {
  key: string;
  title: string;
  type: ArtifactColumnType;
  isMeasure: boolean;
  isContext: boolean;
  period?: ArtifactMeasurePeriod;
  periodRange?: ArtifactPeriodRange;
  format?: unknown;
  labelKey?: string;
}

export interface ArtifactSourceRow {
  [key: string]: unknown;
}

export interface ArtifactSourceLoading {
  status: "loading";
}

export interface ArtifactDrillCells {
  rows: string[][];
  totals: string[];
}

export interface ArtifactSourceReady {
  status: "ready";
  columns: ArtifactSourceColumn[];
  rows: ArtifactSourceRow[];
  rowCount: number;
  totals: Partial<ArtifactSourceRow>;
  drillCells?: ArtifactDrillCells;
}

export interface ArtifactSourceError {
  status: "error";
  message: string;
}

export type ArtifactSourceState = ArtifactSourceLoading | ArtifactSourceReady | ArtifactSourceError;

export interface ArtifactTrailStep {
  view: string;
  params: Record<string, string[]>;
}

export interface ArtifactFile {
  path: string;
  text: string;
}

export interface InitMessage {
  type: "init";
  version: number;
  files: ArtifactFile[];
  sources: ArtifactSourceRef[];
  filters: ArtifactFilterRef[];
  filterValues: Record<string, string[]>;
  filterLabels: Record<string, Record<string, string>>;
  views: ArtifactViewRef[];
  style?: ArtifactStyle;
  logo?: string;
  mode: ArtifactThemeMode;
  localeCode: string;
  build: boolean;
  drillMode: boolean;
}

export interface ThemeChangedMessage {
  type: "themeChanged";
  version: number;
  mode: ArtifactThemeMode;
}

export interface FiltersChangedMessage {
  type: "filtersChanged";
  version: number;
  values: Record<string, string[]>;
  labels: Record<string, Record<string, string>>;
}

export interface SourceStateMessage {
  type: "sourceState";
  version: number;
  subscriptionId: string;
  state: ArtifactSourceState;
}

export interface OpenViewMessage {
  type: "openView";
  version: number;
  viewKey: string;
  params: Record<string, string[]>;
}

export interface DrillModeChangedMessage {
  type: "drillModeChanged";
  version: number;
  enabled: boolean;
}

export interface CapturePreviewMessage {
  type: "capturePreview";
  version: number;
}

export interface CaptureCompareMessage {
  type: "captureCompare";
  version: number;
}

export type HostMessage =
  | InitMessage
  | ThemeChangedMessage
  | FiltersChangedMessage
  | SourceStateMessage
  | OpenViewMessage
  | DrillModeChangedMessage
  | CapturePreviewMessage
  | CaptureCompareMessage;

export interface ReadyMessage {
  type: "ready";
  version: number;
}

export interface SubscribeMessage {
  type: "subscribe";
  version: number;
  subscriptionId: string;
  key: string;
  overrides: Record<string, string[]>;
}

export interface UnsubscribeMessage {
  type: "unsubscribe";
  version: number;
  subscriptionId: string;
}

export interface SetFilterMessage {
  type: "setFilter";
  version: number;
  key: string;
  values: string[];
}

export interface ClearFilterMessage {
  type: "clearFilter";
  version: number;
  key: string;
}

export interface ViewChangedMessage {
  type: "viewChanged";
  version: number;
  view: string;
  trail: ArtifactTrailStep[];
}

export interface RenderedMessage {
  type: "rendered";
  version: number;
  mode: ArtifactThemeMode;
  view: string;
  problems: string[];
}

export interface ErrorMessage {
  type: "error";
  version: number;
  message: string;
  stack?: string;
  view: string;
  mode: ArtifactThemeMode;
  contained: boolean;
}

export interface WarningMessage {
  type: "warning";
  version: number;
  message: string;
}

export interface ArtifactFigureAnchor {
  x: number;
  y: number;
  width: number;
  height: number;
}

export type ArtifactFigureRow = number | "totals";

export interface FigureClickedMessage {
  type: "figureClicked";
  version: number;
  subscriptionId: string;
  row?: ArtifactFigureRow;
  columnKey: string;
  anchor: ArtifactFigureAnchor;
}

export interface PreviewCapturedMessage {
  type: "previewCaptured";
  version: number;
  image?: Blob;
}

export interface CompareCapturedMessage {
  type: "compareCaptured";
  version: number;
  image?: Blob;
}

export type RuntimeMessage =
  | ReadyMessage
  | SubscribeMessage
  | UnsubscribeMessage
  | SetFilterMessage
  | ClearFilterMessage
  | ViewChangedMessage
  | RenderedMessage
  | ErrorMessage
  | WarningMessage
  | FigureClickedMessage
  | PreviewCapturedMessage
  | CompareCapturedMessage;

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value);

const isStringArray = (value: unknown): value is string[] =>
  Array.isArray(value) && value.every((item) => typeof item === "string");

const isRecordOfStrings = (value: unknown): value is Record<string, string> =>
  isRecord(value) && Object.values(value).every((item) => typeof item === "string");

const isRecordOfStringArrays = (value: unknown): value is Record<string, string[]> =>
  isRecord(value) && Object.values(value).every(isStringArray);

const isFilterLabels = (value: unknown): value is Record<string, Record<string, string>> =>
  isRecord(value) && Object.values(value).every(isRecordOfStrings);

const isThemeMode = (value: unknown): value is ArtifactThemeMode => value === "light" || value === "dark";

const isFiniteNumber = (value: unknown): value is number => typeof value === "number" && Number.isFinite(value);

export const isFigureRow = (value: unknown): value is ArtifactFigureRow | undefined =>
  value === undefined || value === "totals" || (typeof value === "number" && Number.isInteger(value) && value >= 0);

const isFigureAnchor = (value: unknown): value is ArtifactFigureAnchor => {
  if (!isRecord(value)) {
    return false;
  }
  const width = value["width"];
  const height = value["height"];
  return (
    isFiniteNumber(value["x"]) &&
    isFiniteNumber(value["y"]) &&
    isFiniteNumber(width) &&
    width >= 0 &&
    isFiniteNumber(height) &&
    height >= 0
  );
};

const isFilterKind = (value: unknown): value is ArtifactFilterKind =>
  value === "Value" || value === "AsOfDate" || value === "Period";

const isArtifactFile = (value: unknown): value is ArtifactFile =>
  isRecord(value) && typeof value["path"] === "string" && typeof value["text"] === "string";

const isSourceRef = (value: unknown): value is ArtifactSourceRef => {
  if (!isRecord(value)) {
    return false;
  }
  return (
    typeof value["key"] === "string" &&
    isStringArray(value["boundFilterKeys"]) &&
    isStringArray(value["linkFilterKeys"])
  );
};

const isFilterRef = (value: unknown): value is ArtifactFilterRef => {
  if (!isRecord(value)) {
    return false;
  }
  return (
    typeof value["key"] === "string" &&
    typeof value["label"] === "string" &&
    isFilterKind(value["kind"]) &&
    typeof value["singleSelect"] === "boolean"
  );
};

const isViewRef = (value: unknown): value is ArtifactViewRef => {
  if (!isRecord(value)) {
    return false;
  }
  return (
    typeof value["key"] === "string" && typeof value["title"] === "string" && isStringArray(value["linkFilterKeys"])
  );
};

const isStyleTokens = (value: unknown): value is ArtifactStyleTokens => {
  if (!isRecord(value)) {
    return false;
  }
  const chart = value["chart"];
  const positive = value["positive"];
  const negative = value["negative"];
  const reference = value["reference"];
  const warning = value["warning"];
  return (
    typeof value["background"] === "string" &&
    typeof value["paper"] === "string" &&
    typeof value["textPrimary"] === "string" &&
    typeof value["textSecondary"] === "string" &&
    typeof value["primary"] === "string" &&
    typeof value["secondary"] === "string" &&
    (chart === undefined || isStringArray(chart)) &&
    (positive === undefined || typeof positive === "string") &&
    (negative === undefined || typeof negative === "string") &&
    (reference === undefined || typeof reference === "string") &&
    (warning === undefined || typeof warning === "string")
  );
};

const isStyle = (value: unknown): value is ArtifactStyle => {
  if (!isRecord(value)) {
    return false;
  }
  const density = value["density"];
  const bodyFont = value["bodyFont"];
  const headingFont = value["headingFont"];
  return (
    isStyleTokens(value["light"]) &&
    isStyleTokens(value["dark"]) &&
    typeof value["radius"] === "number" &&
    (density === "Compact" || density === "Comfortable") &&
    (bodyFont === undefined || typeof bodyFont === "string") &&
    (headingFont === undefined || typeof headingFont === "string")
  );
};

const isTrailStep = (value: unknown): value is ArtifactTrailStep => {
  if (!isRecord(value)) {
    return false;
  }
  return typeof value["view"] === "string" && isRecordOfStringArrays(value["params"]);
};

const isSourceColumn = (value: unknown): value is ArtifactSourceColumn =>
  isRecord(value) &&
  typeof value["key"] === "string" &&
  typeof value["title"] === "string" &&
  typeof value["type"] === "string" &&
  typeof value["isMeasure"] === "boolean" &&
  typeof value["isContext"] === "boolean" &&
  (value["labelKey"] === undefined || typeof value["labelKey"] === "string");

const isDrillCells = (value: unknown): value is ArtifactDrillCells => {
  if (!isRecord(value)) {
    return false;
  }
  const rows = value["rows"];
  return Array.isArray(rows) && rows.every(isStringArray) && isStringArray(value["totals"]);
};

const isSourceState = (value: unknown): value is ArtifactSourceState => {
  if (!isRecord(value)) {
    return false;
  }
  const status = value["status"];
  if (status === "loading") {
    return true;
  }
  if (status === "error") {
    return typeof value["message"] === "string";
  }
  if (status === "ready") {
    const columns = value["columns"];
    const drillCells = value["drillCells"];
    return (
      Array.isArray(columns) &&
      columns.every(isSourceColumn) &&
      Array.isArray(value["rows"]) &&
      isFiniteNumber(value["rowCount"]) &&
      isRecord(value["totals"]) &&
      (drillCells === undefined || isDrillCells(drillCells))
    );
  }
  return false;
};

export const MIN_CAPTURE_WIDTH_PX = 720;

const PNG_DATA_URL_PREFIX = "data:image/png;base64,";

const isPngDataUrl = (value: unknown): value is string =>
  typeof value === "string" && value.startsWith(PNG_DATA_URL_PREFIX);

const isInitMessage = (value: unknown): value is InitMessage => {
  if (!isRecord(value)) {
    return false;
  }
  const files = value["files"];
  const sources = value["sources"];
  const filters = value["filters"];
  const views = value["views"];
  const style = value["style"];
  const logo = value["logo"];
  return (
    Array.isArray(files) &&
    files.every(isArtifactFile) &&
    Array.isArray(sources) &&
    sources.every(isSourceRef) &&
    Array.isArray(filters) &&
    filters.every(isFilterRef) &&
    isRecordOfStringArrays(value["filterValues"]) &&
    isFilterLabels(value["filterLabels"]) &&
    Array.isArray(views) &&
    views.every(isViewRef) &&
    (style === undefined || isStyle(style)) &&
    (logo === undefined || isPngDataUrl(logo)) &&
    isThemeMode(value["mode"]) &&
    typeof value["localeCode"] === "string" &&
    typeof value["build"] === "boolean" &&
    typeof value["drillMode"] === "boolean"
  );
};

export const isHostMessage = (value: unknown): value is HostMessage => {
  if (!isRecord(value)) {
    return false;
  }
  const type = value["type"];
  const version = value["version"];
  if (typeof type !== "string" || typeof version !== "number") {
    return false;
  }
  switch (type) {
    case "init":
      return isInitMessage(value);
    case "themeChanged":
      return isThemeMode(value["mode"]);
    case "filtersChanged":
      return isRecordOfStringArrays(value["values"]) && isFilterLabels(value["labels"]);
    case "sourceState":
      return typeof value["subscriptionId"] === "string" && isSourceState(value["state"]);
    case "openView":
      return typeof value["viewKey"] === "string" && isRecordOfStringArrays(value["params"]);
    case "drillModeChanged":
      return typeof value["enabled"] === "boolean";
    case "capturePreview":
    case "captureCompare":
      return true;
    default:
      return false;
  }
};

export const isRuntimeMessage = (value: unknown): value is RuntimeMessage => {
  if (!isRecord(value)) {
    return false;
  }
  const type = value["type"];
  const version = value["version"];
  if (typeof type !== "string" || typeof version !== "number") {
    return false;
  }
  switch (type) {
    case "ready":
      return true;
    case "subscribe":
      return (
        typeof value["subscriptionId"] === "string" &&
        typeof value["key"] === "string" &&
        isRecordOfStringArrays(value["overrides"])
      );
    case "unsubscribe":
      return typeof value["subscriptionId"] === "string";
    case "setFilter":
      return typeof value["key"] === "string" && isStringArray(value["values"]);
    case "clearFilter":
      return typeof value["key"] === "string";
    case "viewChanged": {
      const trail = value["trail"];
      return typeof value["view"] === "string" && Array.isArray(trail) && trail.every(isTrailStep);
    }
    case "rendered":
      return isThemeMode(value["mode"]) && typeof value["view"] === "string" && isStringArray(value["problems"]);
    case "error": {
      const stack = value["stack"];
      return (
        typeof value["message"] === "string" &&
        (stack === undefined || typeof stack === "string") &&
        typeof value["view"] === "string" &&
        isThemeMode(value["mode"]) &&
        typeof value["contained"] === "boolean"
      );
    }
    case "warning":
      return typeof value["message"] === "string";
    case "figureClicked":
      return (
        typeof value["subscriptionId"] === "string" &&
        isFigureRow(value["row"]) &&
        typeof value["columnKey"] === "string" &&
        isFigureAnchor(value["anchor"])
      );
    case "previewCaptured":
    case "compareCaptured": {
      const image = value["image"];
      return image === undefined || image instanceof Blob;
    }
    default:
      return false;
  }
};
